import { useEffect, useRef } from 'react';
import {
    PRODUCT_FILES_HASH,
    isSamePageProductFilesHref,
    shouldOpenProductFilesFromLocation,
    stripProductFilesDeepLink,
} from '../util/product-files-modal-deep-link';

interface UseProductFilesModalDeepLinkOptions {
    /** Whether the page has files and owns the popup. */
    enabled: boolean;
    /** Whether the files popup is currently open. */
    open: boolean;
    /** Opens the files popup. */
    onOpen: () => void;
}

/**
 * Opens the product files popup from `#product-files` or `?files=1`.
 *
 * CMS copy can link to `#product-files`. A capture-phase click listener
 * opens the popup even when the hash is already set, and the hash is
 * stripped when the popup closes so the same link works again.
 */
export const useProductFilesModalDeepLink = ({
    enabled,
    open,
    onOpen,
}: UseProductFilesModalDeepLinkOptions) => {
    const onOpenRef = useRef(onOpen);
    const wasOpenRef = useRef(open);

    useEffect(() => {
        onOpenRef.current = onOpen;
    }, [onOpen]);

    useEffect(() => {
        if (!enabled || typeof window === 'undefined') return;

        const openFromLocation = () => {
            if (shouldOpenProductFilesFromLocation(window.location)) {
                onOpenRef.current();
            }
        };

        const onClick = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Element)) return;

            const anchor = target.closest('a');
            if (!anchor) return;

            const href = anchor.getAttribute('href') || '';
            if (!isSamePageProductFilesHref(href, window.location)) return;

            event.preventDefault();
            event.stopPropagation();

            if (window.location.hash !== PRODUCT_FILES_HASH) {
                window.history.replaceState(
                    null,
                    '',
                    `${window.location.pathname}${window.location.search}${PRODUCT_FILES_HASH}`,
                );
            }

            onOpenRef.current();
        };

        openFromLocation();
        window.addEventListener('hashchange', openFromLocation);
        document.addEventListener('click', onClick, true);

        return () => {
            window.removeEventListener('hashchange', openFromLocation);
            document.removeEventListener('click', onClick, true);
        };
    }, [enabled]);

    useEffect(() => {
        if (wasOpenRef.current && !open) {
            stripProductFilesDeepLink();
        }
        wasOpenRef.current = open;
    }, [open]);
};
